import { Box, Flex, Spinner, Text, useToken } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IoMdCloseCircle } from 'react-icons/io';
import SuccessCheck from '@/assets/icons/SuccessCheck';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import type { FaceliftDialogProps } from '@/components/Atoms/FaceliftDialog/FaceliftDialog.types';
import { FaceliftProgress } from '@/components/Atoms/FaceliftProgress';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import { useColorModeValue } from '@/components/ui/color-mode-utils';
import { colors } from '@/components/ui/theme';

export interface FaceliftSuccessProgressModalProps
  extends Omit<FaceliftDialogProps, 'children' | 'title'> {
  globalLoading?: boolean;
  isLoading?: boolean;
  timeoutTime?: number;
  isSucceed?: boolean;
  hasError?: boolean;
  title?: string;
  errorMessage?: string;
  successCustomMessage?: string;
}

const FaceliftSuccessProgressModal = ({
  open,
  onOpenChange,
  globalLoading = false,
  isLoading = false,
  timeoutTime = 5000,
  isSucceed = false,
  hasError = false,
  title = 'Thank You!',
  errorMessage = '',
  showCloseBtn = false,
  showBackBtn = false,
  bg,
  color,
  sx,
  successCustomMessage,
  ...dialogProps
}: FaceliftSuccessProgressModalProps) => {
  const [fade, setFade] = useState(false);
  const [timer, setTimer] = useState(0);
  const [progress, setProgress] = useState(0);
  const [internalOpen, setInternalOpen] = useState(globalLoading || open);
  const autoCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [textDarkBase, textLightBase, spinnerColor] = useToken('colors', [
    'text.dark',
    'text.light',
    'brand.solidSecondary',
  ]);
  const defaultMessageColor = useColorModeValue(textDarkBase, textLightBase);

  useEffect(() => {
    if (globalLoading || (hasError && errorMessage)) {
      setInternalOpen(true);
      if (!hasError) {
        setTimer(0);
      }
    }
  }, [globalLoading, hasError, errorMessage]);


  useEffect(() => {
    if (
      (open || isLoading || globalLoading) &&
      !internalOpen &&
      !hasError &&
      !isSucceed
    ) {
      setInternalOpen(true);
      setTimer(0);
    }
  }, [open, isLoading, globalLoading, internalOpen, hasError, isSucceed]);

  useEffect(() => {
    if (isSucceed || hasError) return;

    if (
      ((globalLoading || open) && timer < 3) ||
      (!isLoading && timer >= 3 && timer < 4)
    ) {
      const countdown = setInterval(() => {
        setFade(false);
        setTimeout(() => {
          setTimer((t) => t + 1);
          setFade(true);
        }, 200);
      }, timeoutTime);

      return () => clearInterval(countdown);
    }
  }, [globalLoading, open, timer, isLoading, timeoutTime, isSucceed, hasError]);

  useEffect(() => {
    if (isSucceed || hasError) return;
    const map = [0, 25, 50, 75, 100];
    setProgress(map[timer] ?? 0);
  }, [timer, isSucceed, hasError]);

  useEffect(() => {
    if (isSucceed) {
      setTimer(4);
      setProgress(100);
      setFade(true);
    }
  }, [isSucceed]);

  useEffect(() => {
    const isModalOpen = internalOpen;

    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    if (isModalOpen && (isSucceed || hasError)) {
      autoCloseTimeoutRef.current = setTimeout(() => {
        setInternalOpen(false);
        onOpenChange?.(false);
        autoCloseTimeoutRef.current = null;
      }, 5000);
    }

    return () => {
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }
    };
  }, [internalOpen, onOpenChange, isSucceed, hasError]);

  const getMessage = useCallback(() => {
    if (timer < 2) return "We're finalizing your request..";
    if (timer === 2) return "Hang tight, this won't take long!";
    if (timer === 3)
      return 'We are ensuring everything is perfect before confirming your request.';
    if (timer === 4) return 'Your patience is appreciated.';
    return '';
  }, [timer]);

  const isOpen = internalOpen;

  return (
    <FaceliftDialog
      open={isOpen}
      onOpenChange={(newOpen) => {
        setInternalOpen(newOpen);
        onOpenChange?.(newOpen);
      }}
      showCloseBtn={showCloseBtn}
      showBackBtn={showBackBtn}
      bg={bg}
      color={color}
      sx={{
        width: '400px',
        maxWidth: '90vw',
        ...sx,
      }}
      {...dialogProps}
    >
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap={2}
        p={4}
        textAlign="center"
      >
        <Show when={hasError && !!errorMessage}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            py={2}
          >
            <Box mb={4}>
              <Show when={hasError && !!errorMessage}>
                <IoMdCloseCircle color={colors.errors} size={64} />
              </Show>
            </Box>
            <Box mb={4}>
              <Text fontSize={32} fontWeight={700}>
                Error!
              </Text>
            </Box>
            <Box>
              <Text fontSize="md" fontWeight={400}>
                {errorMessage}
              </Text>
            </Box>
          </Box>
        </Show>

        <Show when={!hasError || !errorMessage}>
          <Show when={progress === 100}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              borderRadius={2}
              opacity={progress === 100 ? 1 : 0}
              transform={progress === 100 ? 'translateX(0)' : 'translateX(100px)'}
              transition="opacity 1.5s ease, transform 1.5s ease"
            >
              <SuccessCheck width={50} height={50} />
            </Box>
          </Show>

          <Show when={progress !== 100}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              borderRadius={2}
              opacity={progress !== 100 ? 1 : 0}
              transform={progress !== 100 ? 'translateY(0)' : 'translateY(20px)'}
              transition="opacity 0.5s ease, transform 0.5s ease"
            >
              <Spinner size="xl" color={spinnerColor} borderWidth="4px" />
            </Box>
          </Show>

          <Text fontSize="xl" fontWeight="bold" color={defaultMessageColor}>
            {isSucceed ? title : 'Request in progress...'}
          </Text>

          <Box
            display="flex"
            height="30px"
            justifyContent="center"
            alignItems="center"
            borderRadius={2}
            opacity={fade ? 1 : 0}
            transform={fade ? 'translateY(0)' : 'translateY(20px)'}
            transition="opacity 0.5s ease, transform 0.5s ease"
          >
            <FaceliftText fontSize="xs" color="gray.500">
              {getMessage()}
            </FaceliftText>
          </Box>

          <Show when={timer === 4 && !!successCustomMessage}>
            <FaceliftText color={'gray.400'} fontSize={'xs'}>{successCustomMessage}</FaceliftText>
          </Show>

          <Flex width="70%" direction="column" gap={1}>
            <FaceliftProgress value={progress} size="md" />
          </Flex>
        </Show>
      </Flex>
    </FaceliftDialog>
  );
};

export default FaceliftSuccessProgressModal;
